import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

import logo from '../../assets/grihvaas-logo.png';
import directorSign from '../../assets/director-sign.png';
import { dateNumeric, initials, phoneWithCountryCode } from '../../lib/format.js';

/**
 * The printed agent ID card, front and back.
 *
 * Fixed millimetre-ish proportions rather than fluid ones: this is an artefact
 * that gets printed and laminated, so it keeps its size on screen and on paper.
 * The palette lives in `theme.css` as `--idcard-*` tokens — it is deliberately
 * independent of the panel's surfaces, which change with the design system.
 */

/**
 * Only the parts of the card that belong to the company.
 *
 * Address, contact and email come from the agent's own record — they are how
 * you reach the person holding the card, not the head office.
 */
const ORG = {
  about:
    'Long-term rentals across India — owners, tenants and verified brokers, on one platform. A DevQuotient product.',
  website: 'www.grihvaas.com',
};

/** The Grihvaas mark. The asset is transparent, so it sits on the card paper. */
function Logo() {
  return <img className="idcard__logo" src={logo} alt="" />;
}

/** The two-tone wave. One per face: the foot of the front, the head of the back. */
function Wave({ flipped = false }) {
  return (
    <svg
      className={`idcard__wave ${flipped ? 'idcard__wave--bottom' : 'idcard__wave--top'}`}
      viewBox="0 0 320 96"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d="M0 0h320v58c-70 26-132-14-198 4C86 71 40 76 0 66z" fill="var(--idcard-navy-deep)" />
      <path d="M0 0h320v42c-74 30-138-10-206 8C78 57 38 62 0 52z" fill="var(--idcard-teal-deep)" />
      <path d="M0 0h320v30c-78 28-142-12-212 6C72 43 36 48 0 38z" fill="var(--idcard-navy)" />
    </svg>
  );
}

function ContactRow({ icon, label, children }) {
  return (
    <div className="idcard__contact">
      <span className="idcard__icon" aria-hidden="true">
        {icon}
      </span>
      <div>
        <div className="idcard__contact-label">{label}</div>
        <div className="idcard__contact-value">{children}</div>
      </div>
    </div>
  );
}

const ICONS = {
  pin: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a7 7 0 0 0-7 7c0 5.2 6.2 12.4 6.5 12.7a.7.7 0 0 0 1 0C12.8 21.4 19 14.2 19 9a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.58 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .58 3.6 1 1 0 0 1-.25 1z" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4.2-8 5-8-5V6l8 5 8-5z" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.9 6h-2.9a15.6 15.6 0 0 0-1.4-3.8A8 8 0 0 1 18.9 8zM12 4.1c.7 1 1.3 2.3 1.7 3.9h-3.4c.4-1.6 1-2.9 1.7-3.9zM4.3 14a8 8 0 0 1 0-4h3.3a17 17 0 0 0 0 4zm.8 2h2.9c.3 1.4.8 2.7 1.4 3.8A8 8 0 0 1 5.1 16zm2.9-8H5.1a8 8 0 0 1 4.3-3.8A15.6 15.6 0 0 0 8 8zM12 19.9c-.7-1-1.3-2.3-1.7-3.9h3.4c-.4 1.6-1 2.9-1.7 3.9zm2.1-5.9H9.9a15 15 0 0 1 0-4h4.2a15 15 0 0 1 0 4zm.5 5.8c.6-1.1 1.1-2.4 1.4-3.8h2.9a8 8 0 0 1-4.3 3.8zM16.4 14a17 17 0 0 0 0-4h3.3a8 8 0 0 1 0 4z" />
    </svg>
  ),
};

export default function AgentIdCard({ agent }) {
  const [qr, setQr] = useState(null);

  // The QR carries a vCard so a phone camera adds the agent to contacts
  // directly, rather than pointing at a URL that needs the panel to be public.
  useEffect(() => {
    if (!agent) return undefined;

    let active = true;
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `N:${agent.name}`,
      `FN:${agent.name}`,
      'ORG:Grihvaas',
      `TITLE:${agent.designation || 'Agent'}`,
      `TEL;TYPE=CELL:${phoneWithCountryCode(agent.phone)}`,
      `EMAIL:${agent.email || ''}`,
      `ADR:;;${agent.address || ''}`,
      `NOTE:${agent.department || ''} · ${agent.employeeId || ''}`,
      'END:VCARD',
    ].join('\n');

    QRCode.toDataURL(vcard, { margin: 0, width: 240, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (active) setQr(url);
      })
      // A missing QR must not take the whole card down — it prints without one.
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [agent]);

  if (!agent) return null;

  return (
    <div className="idcard-pair">
      {/* ---------------- Front ---------------- */}
      <article className="idcard">
        <div className="idcard__front">
          <Logo />
          <div className="idcard__wordmark">GRIHVAAS</div>
          <div className="idcard__rule" />

          <div className="idcard__photo">
            {agent.photoUrl ? (
              <img src={agent.photoUrl} alt="" />
            ) : (
              <span className="idcard__photo-initials">{initials(agent.name)}</span>
            )}
          </div>

          <h3 className="idcard__name">{agent.name || 'Agent name'}</h3>

          <dl className="idcard__facts">
            <dt>Employee ID</dt>
            {/* Allocated on save, so a live preview has nothing to show yet. */}
            <dd>{agent.employeeId || <span className="idcard__pending">Auto-generated</span>}</dd>
            <dt>Designation</dt>
            <dd>{agent.designation || '—'}</dd>
            <dt>Department</dt>
            <dd>{agent.department || '—'}</dd>
            <dt>Joining Date</dt>
            <dd>{dateNumeric(agent.joiningDate)}</dd>
          </dl>
        </div>

        <Wave flipped />
      </article>

      {/* ---------------- Back ---------------- */}
      <article className="idcard">
        <Wave />

        <div className="idcard__back">
          <h4 className="idcard__heading">ABOUT US</h4>
          <p className="idcard__about">{ORG.about}</p>

          <div className="idcard__rule idcard__rule--full" />

          <ContactRow icon={ICONS.pin} label="Address">
            {agent.address || <span className="idcard__pending">Not provided</span>}
          </ContactRow>
          <ContactRow icon={ICONS.phone} label="Contact">
            {agent.phone ? (
              phoneWithCountryCode(agent.phone)
            ) : (
              <span className="idcard__pending">Not provided</span>
            )}
          </ContactRow>
          <ContactRow icon={ICONS.mail} label="Email">
            {agent.email || <span className="idcard__pending">Not provided</span>}
          </ContactRow>
          <ContactRow icon={ICONS.globe} label="Website">
            {ORG.website}
          </ContactRow>

          <div className="idcard__rule idcard__rule--full" />

          <h4 className="idcard__heading">INSTRUCTIONS</h4>
          <ul className="idcard__instructions">
            <li>This ID card is the property of Grihvaas.</li>
            <li>It must be worn at all times while on duty.</li>
            <li>If found, please return to the above address.</li>
          </ul>

          {/*
            Signature and QR are absolutely placed rather than laid out in
            flow: they sit in the card's bottom margin, which the flowing
            content above must be free to grow into without pushing them
            around.
          */}
          <div className="idcard__sign">
            <img className="idcard__signature" src={directorSign} alt="" />
            <span className="idcard__sign-label">Authorized Signatory</span>
          </div>

          {qr ? (
            <img className="idcard__qr" src={qr} alt="Agent contact QR code" />
          ) : (
            <div className="idcard__qr" />
          )}
        </div>
      </article>
    </div>
  );
}
